import * as d3 from 'd3'
import * as d3Hierarchy from 'd3-hierarchy'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import {
  HierarchyLink,
  JobCatalogNodeData,
  PointNode,
} from 'modules/job_catalog/pages/JobCatalogTreePage/types'
import type { BoundingBox } from 'modules/people_chart/lib/BoundingBox'
import type { Coordinates } from 'modules/people_chart/lib/Coordinates'

export const NODE_SIZE = [160, 160] as const
const NODE_MARGIN = 40 as const

interface ReturnType {
  tree: PointNode<JobCatalogNodeData>[]
  buildTree: (data: d3.HierarchyNode<HierarchyLink>) => PointNode<JobCatalogNodeData>[]
  links: d3Hierarchy.HierarchyPointLink<HierarchyLink>[]
  treeBoundary: BoundingBox
  findNodeById: (nodeId: string) => PointNode<JobCatalogNodeData> | undefined
}

const useJobCatalogTree = (
  hierarchyData: HierarchyLink[],
  width: number,
  nodeData: JobCatalogNodeData[],
  uncollapsedIds: Set<string> = new Set()
): ReturnType => {
  const initialCoordinates = useUnsafeMemo<Coordinates>(() => {
    const [nodeWidth, nodeHeight] = NODE_SIZE
    const initialX = width / 2 - nodeWidth / 2
    const initialY = nodeHeight / 2

    return [initialX, initialY]
  }, [width])

  const childrenMap = useUnsafeMemo(() => {
    const map = new Map<string, string[]>()

    hierarchyData.forEach((node) => {
      if (node.parentId) {
        if (!map.has(node.parentId)) {
          map.set(node.parentId, [])
        }
        map.get(node.parentId)?.push(node.id)
      }
    })

    return map
  }, [hierarchyData])

  const processedHierarchy = useUnsafeMemo(() => {
    const processedData = [...hierarchyData]

    return processedData.filter((node) => {
      if (node.parentId === null || node.parentId === '0') {
        return true
      }

      let currentParentId: string | null = node.parentId
      while (currentParentId && currentParentId !== '0') {
        if (!uncollapsedIds.has(currentParentId)) {
          return false
        }

        const parentNode = hierarchyData.find((n) => n.id === currentParentId)
        currentParentId = parentNode?.parentId || null
      }

      return true
    })
  }, [hierarchyData, uncollapsedIds])

  const hierarchy = useUnsafeMemo(() => {
    try {
      return d3Hierarchy.stratify<HierarchyLink>()(processedHierarchy)
    } catch (err) {
      return null
    }
  }, [processedHierarchy])

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const links = (hierarchy?.links() as d3Hierarchy.HierarchyPointLink<HierarchyLink>[]) || []

  const buildTree = useUnsafeCallback(
    (root: d3.HierarchyNode<HierarchyLink>) => {
      const SIBLINGS_SEPARATION = 1.2
      const [nodeWidth, nodeHeight] = NODE_SIZE
      const treeLayout = d3Hierarchy
        .tree<HierarchyLink>()
        .nodeSize([nodeWidth + NODE_MARGIN, nodeHeight + NODE_MARGIN])
        .separation((_nodeA, _nodeB) => {
          return SIBLINGS_SEPARATION
        })
      const [initialX, initialY] = initialCoordinates
      const yWithoutGhostParent = initialY - nodeHeight
      const treeNodes = treeLayout(root).descendants()

      treeNodes.forEach((node) => {
        node.x = node.x + initialX
        node.y = node.y + yWithoutGhostParent
      })

      return treeNodes
        .map((node) => {
          if (!node.id) return null
          const nodeDataItem = nodeData.find((item) => item.id === node.id)
          const hasChildren = !!childrenMap.get(node.id)?.length
          const isUncollapsed = uncollapsedIds.has(node.id)

          // eslint-disable-next-line no-type-assertion/no-type-assertion
          return {
            ...node,
            data: {
              ...(nodeDataItem || {
                id: node.id,
                name: node.id,
                type: node.data.type,
                parentId: node.data.parentId,
                children: [],
              }),
              hasChildren,
              isUncollapsed,
            },
          } as unknown as PointNode<JobCatalogNodeData>
        })
        .filter((node): node is PointNode<JobCatalogNodeData> => node !== null)
    },
    [initialCoordinates, nodeData, childrenMap, uncollapsedIds]
  )

  const tree = useUnsafeMemo(() => (hierarchy ? buildTree(hierarchy) : []), [hierarchy, buildTree])

  const treeBoundary = useUnsafeMemo(() => {
    const [nodeWidth, nodeHeight] = NODE_SIZE
    const minX = d3.min(tree, (d) => d.x) || 0
    const maxX = d3.max(tree, (d) => d.x) || 0
    const minY = d3.min(tree, (d) => (d.data.type === 'root' ? NaN : d.y)) || 0
    const maxY = d3.max(tree, (d) => d.y + nodeHeight) || 0

    return {
      x0: minX - NODE_MARGIN,
      x1: maxX + nodeWidth + NODE_MARGIN,
      y0: minY - NODE_MARGIN,
      y1: maxY + nodeHeight + NODE_MARGIN,
    }
  }, [tree])

  const findNodeById = useUnsafeCallback(
    (nodeId: string): PointNode<JobCatalogNodeData> | undefined => {
      return tree.find((node) => node.id === nodeId)
    },
    [tree]
  )

  return {
    tree,
    buildTree,
    links,
    treeBoundary,
    findNodeById,
  }
}

export default useJobCatalogTree
